// Building blocks condivisi per le pagine informative / legali (Privacy, Carta
// dei Servizi, Trasparenza tariffaria). Porting da design-reference/project/info-shared.jsx.
import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react'
import { Icons } from './Icons'

export type IconKey = keyof typeof Icons

// Risolve una chiave icona (stringa, dal CMS) nel componente Icons, con fallback se la
// chiave è assente o sconosciuta — così un valore editato a mano non rompe il render.
export function iconByKey(name: string | undefined, fallback: IconKey = 'info') {
  return Icons[(name && name in Icons ? name : fallback) as IconKey]
}

// Pillole di metadati (.doc-meta): icona (chiave Icons) + testo.
export function DocMeta({ pills, style }: { pills: { icon: string; text: string }[]; style?: CSSProperties }) {
  if (pills.length === 0) return null
  return (
    <div className="doc-meta" style={style}>
      {pills.map((p, i) => {
        const PillIcon = iconByKey(p.icon)
        return <span className="pill" key={i}><PillIcon /> {p.text}</span>
      })}
    </div>
  )
}

// Callout informativo (.doc-callout); il contenuto è HTML sanitizzato dal CMS.
export function DocCallout({ html, style }: { html: string; style?: CSSProperties }) {
  return (
    <div className="doc-callout" style={style}>
      <Icons.info />
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

// Hero compatto (scuro) per le pagine documentali — eyebrow chip + h1 + lead.
export function DocHero({ eyebrowIcon, eyebrow, title, titleAccent, lead }: {
  eyebrowIcon?: string; eyebrow: string; title: string; titleAccent?: string; lead?: string
}) {
  const EyebrowIcon = eyebrowIcon ? iconByKey(eyebrowIcon) : null
  return (
    <section className="page-hero info-hero">
      <div className="page-hero-inner">
        <div>
          <div className="eyebrow">
            {EyebrowIcon && <span className="ic"><EyebrowIcon /></span>}
            {eyebrow}
          </div>
          <h1>{title} {titleAccent && <span className="g">{titleAccent}</span>}</h1>
          {lead && <p className="lead">{lead}</p>}
        </div>
      </div>
    </section>
  )
}

// Riga di download di un singolo PDF (scheda). Lo stile "muted" per l'archivio
// è guidato dal contenitore .dl-archive via CSS, non da una prop.
export function DocDL({ name, sub = 'PDF · AGCOM', href = '#' }: {
  name: string; sub?: string; href?: string
}) {
  return (
    <a className="dl-card" href={href} target="_blank" rel="noopener noreferrer">
      <span className="dl-ic"><Icons.doc /><span className="ext">PDF</span></span>
      <span className="dl-tx">
        <span className="dl-nm">{name}</span>
        <span className="dl-sb">{sub}</span>
      </span>
      <span className="dl-go"><Icons.download /></span>
    </a>
  )
}

export type DocSection = { id: string; label: string }

// Indice laterale sticky con scroll-spy: evidenzia la sezione corrente e fa
// smooth-scroll con offset per l'header fisso.
export function DocToc({ sections, label = 'In questa pagina' }: {
  sections: DocSection[]; label?: string
}) {
  const [active, setActive] = useState(sections[0]?.id)

  useEffect(() => {
    const spy = () => {
      const line = window.innerHeight * 0.28
      let cur = sections[0]?.id
      for (const s of sections) {
        const el = document.getElementById(s.id)
        if (el && el.getBoundingClientRect().top - line <= 0) cur = s.id
      }
      setActive(cur)
    }
    window.addEventListener('scroll', spy, { passive: true })
    window.addEventListener('resize', spy)
    spy()
    return () => {
      window.removeEventListener('scroll', spy)
      window.removeEventListener('resize', spy)
    }
  }, [sections])

  const go = (e: MouseEvent, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 84
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  return (
    <nav className="doc-toc" aria-label="Indice">
      <div className="doc-toc-label">{label}</div>
      <ol>
        {sections.map((s, i) => (
          <li key={s.id}>
            <a href={`#${s.id}`} className={active === s.id ? 'is-active' : ''} onClick={(e) => go(e, s.id)}>
              <span className="tn">{String(i + 1).padStart(2, '0')}</span>
              <span>{s.label}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}
